import { ALL_ELEMENTS, getEquipmentById, getVehicleChassisById, getWeaponById } from "@/data";
import { VEHICLE_WEAPONS } from "@/data/vehicles";
import type { ForceElement, ForceList, ForceModel, ForceVehicle } from "@/types/force";

/** Calculate the cost of a single model's loadout (weapons + equipment) */
export function calculateModelLoadoutCost(model: ForceModel): number {
	let cost = 0;
	for (const weaponId of model.weapons) {
		const weapon = getWeaponById(weaponId);
		if (weapon) cost += weapon.cost;
	}
	for (const equipId of model.equipment) {
		const equip = getEquipmentById(equipId);
		if (equip) cost += equip.cost;
	}
	return cost;
}

/**
 * Calculate the base cost of a model (based on tier and role).
 *
 * For standard elements: returns the full per-model tier cost.
 * For base-cost elements (expansion factions): returns only the upgrade delta
 * (tier upgrade + sergeant promotion). The flat element cost is handled in
 * calculateElementCost.
 */
export function calculateModelBaseCost(model: ForceModel, element: ForceElement): number {
	const template = ALL_ELEMENTS.find((t) => t.id === element.templateId);
	if (!template) return 0;

	// Base-cost elements: soldierCosts represent upgrade deltas
	if (template.baseCost != null) {
		let cost = 0;
		if (model.role === "commander") {
			cost += template.commanderCosts[element.tier] ?? 0;
		} else {
			cost += template.soldierCosts[element.tier] ?? 0;
		}
		if (model.role === "sergeant") {
			cost += template.sergeantUpgradeCost;
		}
		return cost;
	}

	// Standard per-model tier cost
	if (model.role === "commander") {
		return template.commanderCosts[element.tier] ?? 0;
	}

	let baseCost = template.soldierCosts[element.tier] ?? 0;
	if (model.role === "sergeant") {
		baseCost += template.sergeantUpgradeCost;
	}
	return baseCost;
}

/** Calculate total cost of a single model */
export function calculateModelTotalCost(model: ForceModel, element: ForceElement): number {
	return calculateModelBaseCost(model, element) + calculateModelLoadoutCost(model);
}

/**
 * Calculate total cost of an element.
 *
 * For base-cost elements: baseCost + extra models * additionalModelCost + per-model upgrades/loadout
 * For standard elements: sum of per-model total costs
 */
export function calculateElementCost(element: ForceElement): number {
	const template = ALL_ELEMENTS.find((t) => t.id === element.templateId);

	// Sum of all individual model costs (for base-cost: upgrade deltas + loadout)
	const modelsCost = element.models.reduce(
		(sum, model) => sum + calculateModelTotalCost(model, element),
		0,
	);

	if (template?.baseCost != null) {
		const extraModels = Math.max(0, element.models.length - template.minModels);
		return template.baseCost + extraModels * (template.additionalModelCost ?? 0) + modelsCost;
	}

	return modelsCost;
}

/** Calculate total cost of a vehicle */
export function calculateVehicleCost(vehicle: ForceVehicle): number {
	const chassis = getVehicleChassisById(vehicle.chassisId);
	if (!chassis) return 0;

	let cost = chassis.baseCost;
	for (const mount of vehicle.mountSelections) {
		if (mount.weaponId) {
			const weapon = VEHICLE_WEAPONS.find((w) => w.id === mount.weaponId);
			if (weapon) cost += weapon.cost;
		}
	}
	return cost;
}

/** Calculate the total cost of the entire force */
export function calculateForceTotalCost(force: ForceList): number {
	const elementsCost = force.elements.reduce((sum, el) => sum + calculateElementCost(el), 0);
	const vehiclesCost = force.vehicles.reduce((sum, v) => sum + calculateVehicleCost(v), 0);
	return elementsCost + vehiclesCost;
}

/** Get a cost breakdown for the force */
export function getForceCostBreakdown(force: ForceList) {
	const elements = force.elements.map((el) => ({
		id: el.id,
		templateId: el.templateId,
		cost: calculateElementCost(el),
	}));
	const vehicles = force.vehicles.map((v) => ({
		id: v.id,
		chassisId: v.chassisId,
		cost: calculateVehicleCost(v),
	}));
	return {
		elements,
		vehicles,
		totalElementsCost: elements.reduce((s, e) => s + e.cost, 0),
		totalVehiclesCost: vehicles.reduce((s, v) => s + v.cost, 0),
		totalCost: calculateForceTotalCost(force),
	};
}
